import React, { useCallback, useEffect, useReducer, useState } from "react";
import { toast } from "react-toastify";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import AddAlarmIcon from "@material-ui/icons/AddAlarm";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import SearchIcon from "@material-ui/icons/Search";
import WatchLaterIcon from "@material-ui/icons/WatchLater";
import RepeatIcon from "@material-ui/icons/Repeat";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ScheduledIndividualMessageModal from "../../components/ScheduledIndividualMessageModal";
import toastError from "../../errors/toastError";
import { listScheduledMessages, deleteScheduledMessage } from "../../services/scheduledIndividualMessages.api";
import moment from "moment";

// ─────────────────────────────────────────────────────────────────────────────
// Estilos
// ─────────────────────────────────────────────────────────────────────────────
const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    overflowX: "auto",
    ...theme.scrollbarStyles,
    borderRadius: 12,
    boxShadow: theme.mode === "dark"
      ? "0 4px 20px rgba(0,0,0,0.3)"
      : "0 4px 20px rgba(0,0,0,0.1)",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#e0e0e0"}`,
  },
  tableHeader: {
    "& th": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      fontWeight: 700,
      fontSize: "0.8rem",
      padding: theme.spacing(1.5),
      whiteSpace: "nowrap",
    },
  },
  tableRow: {
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "#374151" : theme.palette.action.hover,
    },
    "& td": {
      padding: theme.spacing(1.2),
      borderBottom: `1px solid ${theme.mode === "dark" ? "#4B5563" : "rgba(224,224,224,0.5)"}`,
      fontSize: "0.82rem",
    },
  },
  searchField: {
    width: 280,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: theme.mode === "dark" ? "#374151" : "#f5f5f5",
    },
  },
  bodyCell: {
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  deleteBtn: {
    color: "#ef4444",
    padding: 5,
    "&:hover": { backgroundColor: "rgba(239,68,68,0.08)" },
  },
  editBtn: {
    color: "#3b82f6",
    padding: 5,
    "&:hover": { backgroundColor: "rgba(59,130,246,0.08)" },
  },
  hideBtn: {
    color: "#9ca3af",
    padding: 5,
    "&:hover": { backgroundColor: "rgba(156,163,175,0.08)" },
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// Estado / colores de los badges
// ─────────────────────────────────────────────────────────────────────────────
const STATUS_CFG = {
  pending:   { label: "Pendiente",  color: "#f59e0b" },
  sending:   { label: "Enviando",   color: "#3b82f6" },
  sent:      { label: "Enviado",    color: "#10b981" },
  completed: { label: "Completado", color: "#10b981" },
  failed:    { label: "Fallido",    color: "#ef4444" },
  cancelled: { label: "Cancelado",  color: "#9ca3af" },
  paused:    { label: "Pausado",    color: "#f59e0b" },
};

const PAGE_SIZE = 20;

// ─────────────────────────────────────────────────────────────────────────────
// Reducer
// ─────────────────────────────────────────────────────────────────────────────
const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD":  return action.payload;
    case "DELETE": return state.filter(r => r.id !== action.payload);
    case "RESET":  return [];
    default:       return state;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
const ScheduledMessages = () => {
  const classes = useStyles();

  const [records, dispatch] = useReducer(reducer, []);
  const [loading, setLoading]           = useState(false);
  const [searchParam, setSearchParam]   = useState("");
  const [page, setPage]                 = useState(0);
  const [totalCount, setTotalCount]     = useState(0);
  const [confirmOpen, setConfirmOpen]   = useState(false);
  const [targetId, setTargetId]         = useState(null);
  const [targetLabel, setTargetLabel]   = useState("");
  const [targetAction, setTargetAction] = useState("cancel"); // "cancel" | "hide"
  const [simModalOpen, setSimModalOpen] = useState(false);
  const [editRecord, setEditRecord]     = useState(null);

  // ── Carga de datos ─────────────────────────────────────────────────────────
  const fetchRecords = useCallback(async (pageNum = 1, search = "") => {
    setLoading(true);
    try {
      const { data } = await listScheduledMessages({ pageNumber: pageNum, searchParam: search });
      dispatch({ type: "LOAD", payload: data.records || [] });
      setTotalCount(data.count || 0);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce en búsqueda
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(0);
      fetchRecords(1, searchParam);
    }, 500);
    return () => clearTimeout(t);
  }, [searchParam, fetchRecords]);

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    fetchRecords(newPage + 1, searchParam);
  };

  // ── Abrir modal de edición ──────────────────────────────────────────────────
  const openEditModal = (rec) => {
    setEditRecord(rec);
    setSimModalOpen(true);
  };

  // ── Eliminar / ocultar ──────────────────────────────────────────────────────
  const openDeleteModal = (rec) => {
    const contact = rec.contact?.name || `Contacto #${rec.contactId}`;
    const date    = moment(rec.nextExecutionAt).format("DD/MM/YY HH:mm");
    const isPending = ["pending", "paused"].includes(rec.status);
    setTargetId(rec.id);
    setTargetLabel(`${contact} — ${date}`);
    setTargetAction(isPending ? "cancel" : "hide");
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteScheduledMessage(targetId);
      dispatch({ type: "DELETE", payload: targetId });
      setTotalCount(c => c - 1);
      toast.success(
        targetAction === "cancel"
          ? "Recordatorio cancelado correctamente."
          : "Registro ocultado del panel."
      );
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmOpen(false);
      setTargetId(null);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const renderStatus = status => {
    const cfg = STATUS_CFG[status] || { label: status, color: "#9ca3af" };
    return (
      <Chip
        size="small"
        label={cfg.label}
        style={{ backgroundColor: cfg.color, color: "#fff", fontWeight: 700, fontSize: "0.68rem" }}
      />
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <MainContainer>
      <ConfirmationModal
        title={targetAction === "cancel" ? "Cancelar recordatorio" : "Ocultar del panel"}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      >
        {targetAction === "cancel" ? (
          <>¿Confirmas la cancelación del recordatorio para{" "}
          <strong>{targetLabel}</strong>? El mensaje no se enviará.</>
        ) : (
          <>¿Ocultar <strong>{targetLabel}</strong> de tu panel? El registro se conserva para auditorías de administración.</>
        )}
      </ConfirmationModal>

      <MainHeader>
        <Box display="flex" alignItems="center" style={{ gap: 10 }}>
          <WatchLaterIcon style={{ color: "#0056b3", fontSize: 26 }} />
          <Title>Mensajes Individuales Programados</Title>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 10 }}>
          <TextField
            placeholder="Buscar por mensaje..."
            variant="outlined"
            size="small"
            className={classes.searchField}
            value={searchParam}
            onChange={e => setSearchParam(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddAlarmIcon />}
            onClick={() => { setEditRecord(null); setSimModalOpen(true); }}
            style={{ whiteSpace: "nowrap" }}
          >
            Nuevo Recordatorio
          </Button>
        </Box>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>Mensaje</TableCell>
              <TableCell>Canal</TableCell>
              <TableCell>Agente</TableCell>
              <TableCell>Fecha / Próximo envío</TableCell>
              <TableCell>Frecuencia</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={8} />
            ) : records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" style={{ padding: "32px 0", color: "#9ca3af" }}>
                  No hay mensajes programados.
                </TableCell>
              </TableRow>
            ) : (
              records.map(rec => {
                const contact    = rec.contact?.name   || `#${rec.contactId}`;
                const number     = rec.contact?.number || "";
                const channel    = rec.whatsapp?.name  || rec.whatsapp?.number || `Canal #${rec.whatsappId}`;
                const agent      = rec.user?.name      || `Usuario #${rec.userId}`;
                const nextDate   = moment(rec.nextExecutionAt).format("DD/MM/YY HH:mm");
                const isRecurring  = rec.recurrenceType !== "once";
                const canEdit     = ["pending", "paused"].includes(rec.status);
                const canCancel   = canEdit;
                const canHide     = ["sent", "completed", "failed", "cancelled"].includes(rec.status);

                return (
                  <TableRow key={rec.id} className={classes.tableRow}>
                    {/* Cliente */}
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 600, lineHeight: 1.3 }}>
                        {contact}
                      </Typography>
                      {number && (
                        <Typography variant="caption" style={{ color: "#6b7280" }}>
                          {number}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Mensaje */}
                    <TableCell>
                      <Tooltip title={rec.body} placement="top">
                        <Typography variant="body2" className={classes.bodyCell}>
                          {rec.body}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Canal */}
                    <TableCell>
                      <Typography variant="body2">{channel}</Typography>
                    </TableCell>

                    {/* Agente */}
                    <TableCell>
                      <Typography variant="body2">{agent}</Typography>
                    </TableCell>

                    {/* Fecha */}
                    <TableCell>
                      <Typography variant="body2" style={{ whiteSpace: "nowrap" }}>
                        {nextDate}
                      </Typography>
                    </TableCell>

                    {/* Frecuencia */}
                    <TableCell>
                      {isRecurring ? (
                        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                          <RepeatIcon style={{ fontSize: 14, color: "#6b7280" }} />
                          <Typography variant="caption" style={{ color: "#6b7280", textTransform: "capitalize" }}>
                            {rec.recurrenceType}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" style={{ color: "#9ca3af" }}>Única vez</Typography>
                      )}
                    </TableCell>

                    {/* Estado */}
                    <TableCell>{renderStatus(rec.status)}</TableCell>

                    {/* Acciones */}
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 2 }}>
                        {/* Editar — solo para pending/paused */}
                        <Tooltip title={canEdit ? "Editar recordatorio" : "Solo se puede editar en estado pendiente"}>
                          <span>
                            <IconButton
                              size="small"
                              className={classes.editBtn}
                              disabled={!canEdit}
                              onClick={() => openEditModal(rec)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        {/* Cancelar (pending) o Ocultar (sent/completed) */}
                        {canCancel && (
                          <Tooltip title="Cancelar recordatorio">
                            <IconButton
                              size="small"
                              className={classes.deleteBtn}
                              onClick={() => openDeleteModal(rec)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canHide && (
                          <Tooltip title="Ocultar del panel">
                            <IconButton
                              size="small"
                              className={classes.hideBtn}
                              onClick={() => openDeleteModal(rec)}
                            >
                              <VisibilityOffIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </Paper>

      {/* Modal crear / editar recordatorio */}
      <ScheduledIndividualMessageModal
        open={simModalOpen}
        onClose={() => {
          setSimModalOpen(false);
          setEditRecord(null);
          fetchRecords(page + 1, searchParam);
        }}
        ticketId={null}
        editRecord={editRecord}
      />
    </MainContainer>
  );
};

export default ScheduledMessages;
